import { useEffect, useMemo, useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { Download, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { AttachedFileField } from '@/components/admin/AttachedFileField';
import { ExpiryBadge } from '@/components/admin/ExpiryBadge';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { DataTable, arrIncludesSome, type FacetedFilter } from '@/components/ui/DataTable';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { useAuth } from '@/app/AuthProvider';
import {
  openAdminFile,
  useCompanyCredentials,
  useDeleteCompanyCredential,
  useSaveCompanyCredential,
  type CompanyCredential,
} from '@/features/admin/hooks';
import {
  CREDENTIAL_KINDS,
  CREDENTIAL_KIND_LABELS,
  friendlyAdminError,
} from '@/features/admin/model';
import { formatDateOnly } from '@/lib/formatters';
import { canWrite } from '@/lib/permissions';
import type { CompanyCredentialKind } from '@/types/database';

export function LicensesPage() {
  const { role } = useAuth();
  const canEdit = canWrite(role, 'company_admin');
  const { data, isLoading } = useCompanyCredentials();
  const [editing, setEditing] = useState<CompanyCredential | null>(null);
  const [creating, setCreating] = useState(false);
  const rows = useMemo(() => data ?? [], [data]);

  const columns = useMemo<ColumnDef<CompanyCredential>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Name',
        size: 240,
        cell: ({ row }) => <span className="font-medium text-foreground">{row.original.name}</span>,
      },
      {
        id: 'kind',
        accessorFn: (row) => row.kind,
        header: 'Kind',
        size: 160,
        filterFn: arrIncludesSome,
        cell: ({ row }) => (
          <Badge variant="secondary">{CREDENTIAL_KIND_LABELS[row.original.kind]}</Badge>
        ),
      },
      {
        id: 'issuer',
        accessorFn: (row) => row.issuer ?? '',
        header: 'Issuer',
        size: 180,
        cell: ({ row }) => row.original.issuer || <Muted />,
      },
      {
        id: 'identifier',
        accessorFn: (row) => row.identifier ?? '',
        header: 'Number',
        size: 160,
        cell: ({ row }) =>
          row.original.identifier ? (
            <span className="font-mono text-xs">{row.original.identifier}</span>
          ) : (
            <Muted />
          ),
      },
      {
        id: 'expires_on',
        accessorFn: (row) => row.expires_on ?? '',
        header: 'Expires',
        size: 140,
        cell: ({ row }) =>
          row.original.expires_on ? formatDateOnly(row.original.expires_on) : <Muted />,
      },
      {
        id: 'status',
        accessorFn: (row) => row.expires_on ?? 'none',
        header: 'Status',
        size: 140,
        enableSorting: false,
        cell: ({ row }) => <ExpiryBadge expiresOn={row.original.expires_on} />,
      },
      {
        id: 'file',
        header: 'File',
        size: 90,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.storage_path ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                void openAdminFile(row.original.storage_path!).catch((err) =>
                  toast.error(friendlyAdminError(err, 'Could not open that file.')),
                );
              }}
            >
              <Download className="h-3.5 w-3.5" />
              Open
            </Button>
          ) : (
            <Muted />
          ),
      },
    ],
    [],
  );

  const filters: FacetedFilter[] = useMemo(
    () => [
      {
        columnId: 'kind',
        title: 'Kind',
        options: CREDENTIAL_KINDS.map((kind) => ({
          value: kind,
          label: CREDENTIAL_KIND_LABELS[kind],
        })),
      },
    ],
    [],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Admin"
        title="Licenses & insurance"
        actions={
          canEdit ? (
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              Add
            </Button>
          ) : undefined
        }
      />
      <Card>
        <CardContent className="pt-6">
          <DataTable
            data={rows}
            columns={columns}
            isLoading={isLoading}
            getRowId={(row) => row.id}
            onRowClick={(row) => setEditing(row)}
            searchPlaceholder="Search licenses and policies…"
            facetedFilters={filters}
            tableId="admin-licenses"
            initialPageSize={25}
            emptyState="No licenses or insurance certificates yet."
            mobileCard={{
              title: (row) => row.name,
              subtitle: (row) => row.issuer || row.identifier || '—',
              badges: (row) => [
                <Badge key="kind" variant="secondary">
                  {CREDENTIAL_KIND_LABELS[row.kind]}
                </Badge>,
                <ExpiryBadge key="expiry" expiresOn={row.expires_on} />,
              ],
            }}
          />
        </CardContent>
      </Card>
      <CredentialDialog
        open={creating || !!editing}
        credential={editing}
        canEdit={canEdit}
        onOpenChange={(open) => {
          if (!open) {
            setCreating(false);
            setEditing(null);
          }
        }}
      />
    </div>
  );
}

function Muted() {
  return <span className="text-muted-foreground">—</span>;
}

function CredentialDialog({
  open,
  credential,
  canEdit,
  onOpenChange,
}: {
  open: boolean;
  credential: CompanyCredential | null;
  canEdit: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const save = useSaveCompanyCredential();
  const remove = useDeleteCompanyCredential();
  const [kind, setKind] = useState<CompanyCredentialKind>('business_license');
  const [name, setName] = useState('');
  const [issuer, setIssuer] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [issuedOn, setIssuedOn] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [removeFile, setRemoveFile] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setKind(credential?.kind ?? 'business_license');
    setName(credential?.name ?? '');
    setIssuer(credential?.issuer ?? '');
    setIdentifier(credential?.identifier ?? '');
    setIssuedOn(credential?.issued_on ?? '');
    setExpiresOn(credential?.expires_on ?? '');
    setNotes(credential?.notes ?? '');
    setFile(null);
    setRemoveFile(false);
    setConfirmDelete(false);
  }, [open, credential]);

  const pending = save.isPending || remove.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!canEdit) return;
            void save
              .mutateAsync({
                id: credential?.id,
                kind,
                name,
                issuer,
                identifier,
                issuedOn,
                expiresOn,
                notes,
                file,
                removeFile,
                existingStoragePath: credential?.storage_path,
              })
              .then(() => {
                toast.success(credential ? 'License updated.' : 'License added.');
                onOpenChange(false);
              })
              .catch((err) => toast.error(friendlyAdminError(err, 'Could not save.')));
          }}
        >
          <DialogHeader>
            <DialogTitle>{credential ? credential.name : 'Add a license or policy'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="credential-name">Name</Label>
              <Input
                id="credential-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                disabled={!canEdit}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>Kind</Label>
              <Select
                value={kind}
                onValueChange={(value) => setKind(value as CompanyCredentialKind)}
                disabled={!canEdit}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CREDENTIAL_KINDS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {CREDENTIAL_KIND_LABELS[option]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="credential-issuer">Issuer</Label>
              <Input
                id="credential-issuer"
                value={issuer}
                onChange={(event) => setIssuer(event.target.value)}
                disabled={!canEdit}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="credential-number">License or policy number</Label>
              <Input
                id="credential-number"
                value={identifier}
                onChange={(event) => setIdentifier(event.target.value)}
                disabled={!canEdit}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="credential-issued">Issued</Label>
              <Input
                id="credential-issued"
                type="date"
                value={issuedOn}
                onChange={(event) => setIssuedOn(event.target.value)}
                disabled={!canEdit}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="credential-expires">Expires</Label>
              <Input
                id="credential-expires"
                type="date"
                value={expiresOn}
                onChange={(event) => setExpiresOn(event.target.value)}
                disabled={!canEdit}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="credential-notes">Notes</Label>
              <Textarea
                id="credential-notes"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                disabled={!canEdit}
              />
            </div>
            <div className="sm:col-span-2">
              <AttachedFileField
                existingName={credential?.file_name ?? null}
                file={file}
                removeFile={removeFile}
                disabled={!canEdit}
                onFile={setFile}
                onRemoveFile={setRemoveFile}
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            {canEdit && credential ? (
              confirmDelete ? (
                <Button
                  type="button"
                  variant="destructive"
                  disabled={pending}
                  onClick={() =>
                    void remove
                      .mutateAsync({ id: credential.id, storagePath: credential.storage_path })
                      .then(() => {
                        toast.success('License deleted.');
                        onOpenChange(false);
                      })
                      .catch((err) => toast.error(friendlyAdminError(err, 'Could not delete.')))
                  }
                >
                  Confirm delete
                </Button>
              ) : (
                <Button type="button" variant="outline" onClick={() => setConfirmDelete(true)}>
                  Delete
                </Button>
              )
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              {canEdit && (
                <Button type="submit" disabled={pending || !name.trim()}>
                  Save
                </Button>
              )}
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
